<template>
  <div class="google-map-wrapper position-relative" :style="{ width: '100%', height: height || '480px' }">
    <div
      v-if="mapError"
      class="d-flex flex-column align-center justify-center pa-6 text-center rounded"
      style="width: 100%; height: 100%; background: #F8FAFC; border: 1px dashed #CBD5E1;"
    >
      <v-icon size="40" color="#DC2626" class="mb-2">mdi-map-marker-alert-outline</v-icon>
      <div class="font-weight-bold text-subtitle-2 mb-1" style="color: #1E293B;">تعذر تحميل الخريطة حالياً</div>
      <div class="text-caption text-secondary mb-3" style="max-width: 320px;">
        حدث خطأ أثناء تحميل خدمة الخرائط. يرجى التحقق من الاتصال أو إعادة المحاولة.
      </div>
      <v-btn small depressed color="primary" class="font-weight-bold" @click="retryLoadMap">
        <v-icon left size="16">mdi-refresh</v-icon>
        إعادة المحاولة
      </v-btn>
    </div>

    <template v-else>
      <GmapMap
        :center="center"
        :zoom="zoom"
        ref="mapp"
        :style="{ width: '100%', height: height || '480px' }"
        :options="combinedMapOptions"
        @click="mark"
      >
        <gmap-info-window
          :options="infoOptions"
          :position="infoWindowPos"
          :opened="infoWinOpen"
          @closeclick="infoWinOpen = false"
        >
        </gmap-info-window>

        <GmapMarker
          :key="index"
          v-for="(m, index) in markers"
          :position="m.position"
          :icon="m.icon"
          :options="m.options"
          :zIndex="m.zIndex || 1"
          @click="toggleInfoWindow(m, index)"
        />

        <gmap-polyline
          v-for="(p, index) in normalizedPolylines"
          :key="'poly-' + index"
          :path="p.data"
          :options="{ strokeColor: p.strokeColor, strokeOpacity: p.strokeOpacity, strokeWeight: p.strokeWeight }"
        ></gmap-polyline>
      </GmapMap>

      <v-btn v-if="markers && markers.length > 1" depressed color="primary" @click="fitBounds" class="ma-1 float-right my-2 font-weight-bold">
        {{ $t('fit') || 'إظهار الكل' }}
        <v-icon right dark>
          mdi-fit-to-screen-outline
        </v-icon>
      </v-btn>
    </template>
  </div>
</template>

<script>
import { loadGmapApi, gmapApi } from "vue2-google-maps";
import { Keys } from "@/config.js";

export const BEEPGO_MAP_STYLE = [
  // 1. Reduce unnecessary POIs (shops, restaurants, attractions)
  {
    featureType: "poi",
    elementType: "labels.text",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "poi",
    elementType: "icon",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry.fill",
    stylers: [{ color: "#e8f5e9" }, { visibility: "on" }]
  },
  // 2. Soft neutral land background
  {
    featureType: "landscape",
    elementType: "geometry.fill",
    stylers: [{ color: "#f8f9fa" }]
  },
  // 3. Water - soft clean blue
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{ color: "#d0e8f9" }]
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#0288d1" }]
  },
  // 4. Roads - clean white / subtle blue-gray highways
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#e5e7eb" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.fill",
    stylers: [{ color: "#cfd8dc" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#b0bec5" }]
  },
  // 5. Readable Dark City / Neighborhood Labels
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#111827" }]
  },
  {
    featureType: "administrative.neighborhood",
    elementType: "labels.text.fill",
    stylers: [{ color: "#374151" }]
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#4b5563" }]
  },
  // 6. Reduce transit icons
  {
    featureType: "transit",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }]
  }
];

export default {
  props: {
    center: Object,
    zoom: Number,
    apiKey: String,
    markers: Array,
    selected: String,
    enabled: Boolean,
    polylines: Array,
    mapOptions: Object,
    height: {
      type: String,
      default: '480px',
    },
  },
  data() {
    return {
      mapError: false,
      infoWindowPos: null,
      infoWinOpen: false,
      currentMidx: null,
      infoOptions: {
        content: "",
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
    };
  },
  computed: {
    google: gmapApi,
    resolvedApiKey() {
      return this.apiKey || (Keys && Keys.GOOGLE_MAPS_API_KEY) || process.env.VUE_APP_GOOGLE_MAPS_API_KEY || process.env.VUE_APP_GOOGLE_MAPS_KEY || "";
    },
    combinedMapOptions() {
      const base = {
        styles: BEEPGO_MAP_STYLE,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
      };
      return Object.assign(base, this.mapOptions || {});
    },
    normalizedPolylines() {
      if (!this.polylines || this.polylines.length === 0) return [];
      if (this.polylines[0] && typeof this.polylines[0].lat === 'number') {
        return [{
          data: this.polylines,
          strokeColor: '#082C69',
          strokeOpacity: 0.85,
          strokeWeight: 5,
        }];
      }
      return this.polylines.map((p) => {
        const pathData = p.data || p.path || (Array.isArray(p) ? p : []);
        return {
          data: pathData,
          strokeColor: p.strokeColor || '#082C69',
          strokeOpacity: p.strokeOpacity || 0.85,
          strokeWeight: p.strokeWeight || 5,
        };
      });
    },
  },
  watch: {
    selected: function (newVal, oldVal) {
      let selectedMarkerIndex = -1;
      for (let index = 0; index < this.markers.length; index++) {
        const marker = this.markers[index];
        if (marker.place_id == newVal) {
          selectedMarkerIndex = index;
          break;
        }
      }
      if (selectedMarkerIndex != -1) {
        this.toggleInfoWindow(
          this.markers[selectedMarkerIndex],
          selectedMarkerIndex
        );
      } else {
        this.infoWinOpen = false;
      }
    },
  },
  beforeMount() {
    if (this.google == null) {
      loadGmapApi({
        key: this.resolvedApiKey,
        libraries: "places, geocoding",
      });
    }
  },
  mounted() {
    window.gm_authFailure = () => {
      console.warn("Google Maps auth failure detected in GoogleMapLoader.");
      this.mapError = true;
    };
  },
  methods: {
    retryLoadMap() {
      this.mapError = false;
      loadGmapApi({
        key: this.resolvedApiKey,
        libraries: "places, geocoding",
      });
    },
    mark(event) {
      if (!this.enabled) return;

      const latlng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      let geocoder = new google.maps.Geocoder();
      geocoder
        .geocode({ location: latlng })
        .then((response) => {
          if (response.results[0]) {
            this.$emit("map-click", response.results[0]);
          } else {
            window.alert("No results found");
          }
        })
        .catch((e) => window.alert("Geocoder failed due to: " + e));
    },
    fitBounds() {
      if (!this.markers || this.markers.length === 0) return;
      var b = new google.maps.LatLngBounds();
      this.markers.forEach((marker) => {
        if (marker.position && marker.position.lat && marker.position.lng) {
          b.extend({
            lat: marker.position.lat,
            lng: marker.position.lng,
          });
        }
      });
      if (this.$refs.mapp) {
        this.$refs.mapp.fitBounds(b);
      }
    },
    toggleInfoWindow: function (marker, idx) {
      this.$emit("marker-click", marker);
      if (!marker || !marker.infoText) return;
      this.infoWindowPos = marker.position;
      this.infoOptions.content = marker.infoText;

      if (this.currentMidx == idx) {
        this.infoWinOpen = !this.infoWinOpen;
      } else {
        this.infoWinOpen = true;
        this.currentMidx = idx;
      }
    },
  },
};
</script>
